import React from 'react'
import PropTypes from 'prop-types'

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

// Update fallback data to match the viewings shown in the image (50, 70, 60, 35, 80)
const fallbackChartData = [
  { time: '12:00', viewings: 50 },
  { time: '01:00', viewings: 70 },
  { time: '02:00', viewings: 60 },
  { time: '03:00', viewings: 35 },
  { time: '04:00', viewings: 80 },
]

const ViewingsBarCustom = ({
  data = {},
  title = "", // Change default title to match the image
  color = "#38764e", // Update default color to the deep green from the image
  groupBy = "month",
  height = 250, // Increase height slightly for better visual
  showGrid = true,
  barSize = 50, // Increase bar size for a chunkier look
  yAxisFormat = "number", // Default to number format for this design
  viewType = "default",
  showTitle = false,
  emptyStateMessage = "No data available"
}) => {

  const extractData = (responseData) => {
    if (!responseData) return []

    // Handle the specific structure from your API endpoint
    if (responseData.hourlyViews && Array.isArray(responseData.hourlyViews)) {
      return responseData.hourlyViews
    }

    if (responseData.summary && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data
    }

    if (Array.isArray(responseData)) return responseData

    if (responseData.data && Array.isArray(responseData.data)) return responseData.data

    return []
  }

  const processViewTypeData = (rawData, type) => {
    const extractedData = extractData(rawData)

    if (extractedData.length === 0) return []

    switch (type) {
      case "mobile":
        return processMobileViewsData(extractedData)
      case "desktop":
        return processDesktopViewsData(extractedData)
      case "other":
        return processOtherViewsData(extractedData)
      case "hourly":
        return processHourlyPropertyViewsData(extractedData)
      default:
        return processViewingsData(extractedData)
    }
  }


  const processMobileViewsData = (rawData) => {
    const mobileData = rawData.filter(item =>
      item.deviceType === "mobile" ||
      item.os === "iOS" ||
      item.os === "Android"
    )

    const groupedData = {}

    mobileData.forEach(item => {
      let key
      const date = new Date(item.viewedAt || item.createdAt)

      switch (groupBy) {
        case "device":
          key = item.deviceType === "mobile" ? `${item.os} Mobile` : item.os || "Unknown Device"
          break
        case "os":
          key = item.os || "Unknown OS"
          break
        case "month":
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          key = monthNames[date.getMonth()]
          break
        case "day":
          key = date.toLocaleDateString('en-US', { weekday: 'short' })
          break
        case "hour":
          key = date.getHours().toString().padStart(2, '0') + ':00'
          break
        default:
          key = date.toLocaleDateString()
      }

      if (!groupedData[key]) {
        groupedData[key] = 0
      }
      groupedData[key] += 1
    })

    let result = Object.entries(groupedData).map(([time, viewings]) => ({
      time,
      viewings
    }))

    if (groupBy === "month") {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      result.sort((a, b) => monthOrder.indexOf(a.time) - monthOrder.indexOf(b.time))
    } else if (groupBy === "day") {
      const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      result.sort((a, b) => dayOrder.indexOf(a.time) - dayOrder.indexOf(b.time))
    } else if (groupBy === "hour") {
      result.sort((a, b) => a.time.localeCompare(b.time))
    }

    return result
  }

  const processDesktopViewsData = (rawData) => {
    const desktopData = rawData.filter(item =>
      item.deviceType === "desktop" ||
      (!item.deviceType && (item.os === "Windows" || item.os === "macOS" || item.os === "Linux"))
    )

    const groupedData = {}

    desktopData.forEach(item => {
      let key
      const date = new Date(item.viewedAt || item.createdAt)

      switch (groupBy) {
        case "browser":
          key = item.browser || "Unknown Browser"
          break
        case "os":
          key = item.os || "Unknown OS"
          break
        case "month":
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          key = monthNames[date.getMonth()]
          break
        case "day":
          key = date.toLocaleDateString('en-US', { weekday: 'short' })
          break
        case "hour":
          key = date.getHours().toString().padStart(2, '0') + ':00'
          break
        default:
          key = date.toLocaleDateString()
      }

      if (!groupedData[key]) {
        groupedData[key] = 0
      }
      groupedData[key] += 1
    })

    let result = Object.entries(groupedData).map(([time, viewings]) => ({
      time,
      viewings
    }))

    if (groupBy === "month") {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      result.sort((a, b) => monthOrder.indexOf(a.time) - monthOrder.indexOf(b.time))
    } else if (groupBy === "day") {
      const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      result.sort((a, b) => dayOrder.indexOf(a.time) - dayOrder.indexOf(b.time))
    } else if (groupBy === "hour") {
      result.sort((a, b) => a.time.localeCompare(b.time))
    }

    return result
  }

  const processOtherViewsData = (rawData) => {
    const otherData = rawData.filter(item =>
      !item.deviceType ||
      (item.deviceType !== "mobile" && item.deviceType !== "desktop")
    )

    const dataToProcess = otherData.length > 0 ? otherData : rawData

    const groupedData = {}

    dataToProcess.forEach(item => {
      let key
      const date = new Date(item.viewedAt || item.createdAt)

      switch (groupBy) {
        case "source":
          key = item.referrer || item.source || "Direct"
          break
        case "country":
          key = item.country || "Unknown"
          break
        case "user":
          key = item.user?.full_name || item.user?.email || "Anonymous"
          break
        case "deviceType":
          key = item.deviceType || "Unknown Device"
          break
        case "month":
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          key = monthNames[date.getMonth()]
          break
        case "day":
          key = date.toLocaleDateString('en-US', { weekday: 'short' })
          break
        case "hour":
          key = date.getHours().toString().padStart(2, '0') + ':00'
          break
        default:
          key = date.toLocaleDateString()
      }

      if (!groupedData[key]) {
        groupedData[key] = 0
      }
      groupedData[key] += 1
    })

    let result = Object.entries(groupedData).map(([time, viewings]) => ({
      time,
      viewings
    }))

    if (groupBy === "month") {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      result.sort((a, b) => monthOrder.indexOf(a.time) - monthOrder.indexOf(b.time))
    } else if (groupBy === "day") {
      const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      result.sort((a, b) => dayOrder.indexOf(a.time) - dayOrder.indexOf(b.time))
    } else if (groupBy === "hour") {
      result.sort((a, b) => a.time.localeCompare(b.time))
    }

    return result
  }

  const processHourlyPropertyViewsData = (rawData) => {
    const extractedData = extractData(rawData)

    if (extractedData.length === 0) return []

    // Handle the specific structure from your API endpoint
    if (extractedData[0] && extractedData[0].hour && extractedData[0].views !== undefined) {
      return extractedData
        .filter(item => item.views > 0) // Only show hours with views
        .map(item => ({
          time: item.hour,
          viewings: item.views
        }))
        .sort((a, b) => a.time.localeCompare(b.time))
    }

    const groupedData = {}

    extractedData.forEach(item => {
      let key
      const date = new Date(item.viewedAt || item.createdAt || item.timestamp)

      switch (groupBy) {
        case "hour":
          key = date.getHours().toString().padStart(2, '0') + ':00'
          break
        case "day":
          key = date.toLocaleDateString('en-US', { weekday: 'short' })
          break
        case "month":
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          key = monthNames[date.getMonth()]
          break
        case "property":
          key = item.property?.title || item.propertyTitle || `Property ${item.propertyId || 'Unknown'}`
          break
        case "user":
          key = item.user?.full_name || item.user?.email || item.sessionId || "Anonymous"
          break
        default:
          key = date.getHours().toString().padStart(2, '0') + ':00'
      }

      if (!groupedData[key]) {
        groupedData[key] = 0
      }
      groupedData[key] += item.views || item.count || 1
    })

    let result = Object.entries(groupedData).map(([time, viewings]) => ({
      time,
      viewings
    }))

    if (groupBy === "hour") {
      result.sort((a, b) => a.time.localeCompare(b.time))
    } else if (groupBy === "day") {
      const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      result.sort((a, b) => dayOrder.indexOf(a.time) - dayOrder.indexOf(b.time))
    } else if (groupBy === "month") {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      result.sort((a, b) => monthOrder.indexOf(a.time) - monthOrder.indexOf(b.time))
    }

    return result
  }

  const processViewingsData = (rawData) => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return []
    }

    const groupedData = {}

    rawData.forEach(item => {
      let key
      const date = new Date(item.viewedAt || item.createdAt)

      switch (groupBy) {
        case "browser":
          key = item.browser || "Unknown Browser"
          break
        case "os":
          key = item.os || "Unknown OS"
          break
        case "deviceType":
          key = item.deviceType || "Unknown Device"
          break
        case "month":
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          key = monthNames[date.getMonth()]
          break
        case "day":
          key = date.toLocaleDateString('en-US', { weekday: 'short' })
          break
        case "hour":
          key = date.getHours().toString().padStart(2, '0') + ':00'
          break
        default:
          key = date.toLocaleDateString()
      }

      if (!groupedData[key]) {
        groupedData[key] = 0
      }
      groupedData[key] += 1
    })

    let result = Object.entries(groupedData).map(([time, viewings]) => ({
      time,
      viewings
    }))

    if (groupBy === "month") {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      result.sort((a, b) => monthOrder.indexOf(a.time) - monthOrder.indexOf(b.time))
    } else if (groupBy === "day") {
      const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      result.sort((a, b) => dayOrder.indexOf(a.time) - dayOrder.indexOf(b.time))
    } else if (groupBy === "hour") {
      result.sort((a, b) => a.time.localeCompare(b.time))
    }

    return result
  }

  const processedData = processViewTypeData(data, viewType)

  // Only use fallback data if no processed data is available
  const chartData = processedData.length > 0 ? processedData : fallbackChartData

  const totalViewings = chartData.reduce((sum, item) => sum + item.viewings, 0)
  
  // The image shows a max of 80, so setting yAxisMax to 80 (or 100 as a round number)
  const maxValue = chartData.length > 0 ? Math.max(...chartData.map(item => item.viewings)) : 100
  // Set a clean maximum for the Y-Axis (e.g., next multiple of 10 or 20)
  const yAxisMax = Math.ceil(maxValue / 20) * 20 

  const formatYAxisLabel = (value) => {
    switch (yAxisFormat) {
      case "k":
        if (value >= 1000) {
          return `${(value / 1000).toFixed(0)}k`
        }
        return value.toString()
      case "percentage":
        return `${value}%`
      default:
        return value.toString()
    }
  }

  const formatTooltipValue = (value, name) => {
    switch (yAxisFormat) {
      case "k":
        return [value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value, name]
      case "percentage":
        return [`${value}%`, name]
      default:
        return [value, name]
    }
  }

  const getViewTypeColor = (type) => {
    const colors = {
      mobile: color,
      desktop: color,
      other: color,
      default: color
    }
    return colors[type] || colors.default
  }

  const chartColor = getViewTypeColor(viewType)

  return (
    // Outer container matching the design's card style
    <div className="w-full bg-white rounded-lg p-4">
      
      {/* Main Title (Viewings) */}
      <h3 className="text-sm font-medium text-gray-800 mb-1">{title}</h3>
      {/* Subtitle (Total number of viewings) */}
      {/* <p className="text-xs text-gray-500">Total number of viewings</p> */}
      {/* Total Number (112) */}
      <p className="text-md font-bold text-gray-800 mt-1">{totalViewings}</p>


      {/* Chart Container */}
      <div className="w-full" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            // Adjusted margins to match the design's alignment
            margin={{ top: 10, right: 15, left: -5, bottom: 5 }} 
          >
            {/* Removed showGrid check and simplified CartesianGrid for the solid X-Axis line */}
            {/* <CartesianGrid
              stroke="#000" // Use black/dark color for the bottom X-axis line
              strokeWidth={1}
              vertical={true}
              horizontal={false}
               strokeDasharray="0" // No dashes for a solid line
              // Only draw the horizontal line at y=0, which is implicitly handled by the YAxis baseline or a simple stroke.
              // We'll rely on the YAxis for the ticks and the XAxis for the baseline.
            /> */}

            <XAxis
              dataKey="time"
              axisLine={{ stroke: '#000', strokeWidth: 1 }} // Solid black line for X-Axis
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              padding={{ left: 15, right: 15 }} // Adjusted padding to align bars
            />
            <YAxis
                domain={[0, yAxisMax]}
                tickFormatter={formatYAxisLabel}
                axisLine={true} // Enable Y-axis line
                tickLine={true}
                tick={{ fontSize: 11, fill: '#666' }}
                width={30}
            />
            <Tooltip
              cursor={{ fill: 'rgba(56, 118, 78, 0.1)' }} // Light fill for the deep green color
              contentStyle={{
                borderRadius: '6px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                backgroundColor: 'white',
                fontSize: '12px'
              }}
              labelStyle={{ fontWeight: '500', color: '#333', fontSize: '12px' }}
              itemStyle={{ color: chartColor, fontSize: '12px' }}
              formatter={formatTooltipValue}
            />
            <Bar
              dataKey="viewings"
              fill={chartColor}
              barSize={barSize}
              // Removed radius for squared tops as seen in the design
              radius={0} 
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

ViewingsBarCustom.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  title: PropTypes.string,
  color: PropTypes.string,
  groupBy: PropTypes.oneOf(['month', 'day', 'hour', 'device', 'browser', 'os', 'source', 'country', 'user', 'deviceType', 'property']),
  height: PropTypes.number,
  showGrid: PropTypes.bool,
  barSize: PropTypes.number,
  yAxisFormat: PropTypes.oneOf(['number', 'k', 'percentage']),
  viewType: PropTypes.oneOf(['default', 'mobile', 'desktop', 'other', 'hourly']),
  showTitle: PropTypes.bool,
  emptyStateMessage: PropTypes.string
}

export default ViewingsBarCustom